/** Los dos métodos del constructor de consultas que hacen falta aquí. */
interface Restringible {
  eq(columna: string, valor: unknown): unknown;
  in(columna: string, valores: readonly unknown[]): unknown;
}

/**
 * Ámbito de datos de la sesión: qué clientes puede ver quien está dentro.
 *
 * Las políticas de la base protegen los valores, pero varias vistas dejan leer
 * las filas de otros clientes (nombres de sensores, catálogo de clientes…), y
 * en la versión anterior cada pantalla se acordaba —o no— de filtrar. Aquí la
 * restricción se aplica una sola vez, en la capa de datos: una pantalla nueva
 * queda acotada sin que nadie tenga que recordarlo.
 *
 * No sustituye a la seguridad del servidor: es la garantía de que la consola
 * nunca *pide* lo que el usuario no debe ver, ni lo ofrece en un filtro.
 */
export class AmbitoSesion {
  /** `null` significa sin restricción (superadministrador). */
  private clienteIds: number[] | null = null;

  establecer(ids: number[] | null): void {
    this.clienteIds = ids;
  }

  /** Al cerrar la sesión no debe quedar ámbito: sin sesión no se ve nada útil. */
  limpiar(): void {
    this.clienteIds = null;
  }

  get restringido(): boolean {
    return this.clienteIds !== null;
  }

  permite(clienteId: number | null | undefined): boolean {
    if (this.clienteIds === null) return true;
    if (clienteId === null || clienteId === undefined) return false;
    return this.clienteIds.includes(clienteId);
  }

  /**
   * Restringe una consulta a los clientes visibles.
   * `columna` es donde esa tabla guarda el cliente (`id` en la propia tabla
   * de clientes, `cliente_id` en el resto).
   */
  aplicar<T>(consulta: T, columna = 'cliente_id'): T {
    if (this.clienteIds === null) return consulta;

    const restringible = consulta as Restringible;
    if (this.clienteIds.length === 1) {
      return restringible.eq(columna, this.clienteIds[0]) as T;
    }
    return restringible.in(columna, this.clienteIds) as T;
  }
}

export const ambitoSesion = new AmbitoSesion();
